import { useAuthorization } from 'dx-sdk/build/authorization';
import { Hero, Picture, Typography } from 'dx-sdk/build/components';
import { Theme as ThemeProvider } from 'dx-sdk/build/providers';
import { formatDealerName } from 'pubweb-smokey/dist/utils/formatters';
import React, { useContext } from 'react';
import { Helmet } from 'react-helmet';
import { Link, Redirect } from 'react-router-dom';
import App from '../../../App';
import { DXAccountContext } from '../../../contexts/DXAccountContext';
import FavoritesBadge from '../../../images/svg/FavoritesBadge.svg';
import Loading from '../../../images/svg/icons/Loading';
import * as dealerService from '../../../services/dealerService';
import * as homesService from '../../../services/homesService';
import { fireSegmentEvent, parseInitialPropsContext } from '../../../utils';
import FavoritesGrid from '../../components/FavoritesGrid/FavoritesGrid';
import { isPlannerComplete } from '../../utils/PortalUtils';
import FavoritesStyles from './Favorites.styled';

const Favorites = ({ dealer, homes }) => {
  const dxContext = useContext(DXAccountContext);
  const { isAuthenticated, isLoading } = useAuthorization();

  const questionsAreComplete = isPlannerComplete(
    dxContext?.state?.account?.plannerProgress
  );

  if (!isAuthenticated && !isLoading) {
    return <Redirect to="/myhome-account-login" />;
  }

  return (
    <ThemeProvider theme={{}}>
      <App dealer={dealer} homes={homes}>
        <FavoritesStyles>
          <Helmet>
            <title>Favorites | {formatDealerName(dealer?.dealerName)}</title>
            <meta name="robots" content="noindex" />
          </Helmet>

          {dxContext?.state?.globalLoading ? (
            <div className="loading-screen-panel">
              <Loading width="150px" />
            </div>
          ) : (
            <>
              <Hero
                accent={<Picture src={FavoritesBadge} alt="Favorites Badge" />}
                heading="My Favorites"
                align="left"
                componentsProps={{
                  Container: {
                    style: {
                      maxWidth: '934px',
                    },
                  },
                }}
                content={
                  <>
                    <Typography
                      variant="intro"
                      className="large-p"
                      componentProps={{
                        style: {
                          margin: '0 0 7px 0',
                        },
                      }}
                    >
                      As you begin to narrow in on the home that fits your needs
                      and your lifestyle, use this section to view homes you’ve
                      saved.
                    </Typography>
                    {questionsAreComplete && (
                      <>
                        <Typography
                          variant="caption"
                          className="small-p"
                          componentProps={{
                            style: {
                              margin: '0',
                            },
                          }}
                        >
                          Visit your specially curated Homes for You page to
                          find and save more homes to your favorites page.
                        </Typography>
                        <div className="button-wrap">
                          <Link
                            className="hero-btn"
                            to={{
                              pathname: '/portal/homes-for-you',
                              state: { dealer, homes },
                            }}
                            onClick={() => {
                              fireSegmentEvent('CTA Clicked', {
                                isNav: false,
                                type: 'button',
                                text: 'Homes For You',
                                eventContext: 'portal_favorites_homes_for_you',
                              });
                            }}
                          >
                            HOMES FOR YOU
                          </Link>
                        </div>
                      </>
                    )}
                  </>
                }
              />
              <FavoritesGrid dealer={dealer} homes={homes} />
            </>
          )}
        </FavoritesStyles>
      </App>
    </ThemeProvider>
  );
};

Favorites.getInitialProps = async (ctx) => {
  return await parseInitialPropsContext(ctx, dealerService, homesService).catch(
    (e) => {
      console.error(e);
      return { redirectTo: '/error' };
    }
  );
};

export default Favorites;
